import { Skeleton } from "@mui/material"
import Collapse from "@mui/material/Collapse"
import { Subscribe } from "@react-rxjs/core"
import { PropsWithChildren, useState } from "react"
import { useTranslation } from "react-i18next"

import { LogoShort } from "@/App/Equities/shared/components"
import { useAccountSummary } from "@/App/Equities/shared/services/account"
import {
  ProfitAndLossType,
  useProfitAndLoss,
} from "@/App/Equities/shared/services/positions"
import {
  formatCurrency,
  formatPercentageSigned,
  formatProfitAndLoss,
  getChangeTextColor,
} from "@/App/Equities/shared/utils"
import { Box, Stack, Typography } from "@/generated/material"

import { AppHeaderLocators } from "./locators"

const AccountNumber = () => {
  return useAccountSummary().accountNumber
}

const PortfolioBalance = () => (
  <Typography
    variant="Text md/Medium"
    data-testid={AppHeaderLocators.portfolioBalance}
  >
    {formatCurrency(useAccountSummary().portfolioValue)}
  </Typography>
)

const CashBalance = () => (
  <Typography
    variant="Text md/Medium"
    data-testid={AppHeaderLocators.cashBalance}
  >
    {formatCurrency(useAccountSummary().cash)}
  </Typography>
)

interface ProfitAndLossProps {
  type: ProfitAndLossType
  "data-testid": string
}

const ProfitAndLoss = ({ type, "data-testid": testId }: ProfitAndLossProps) => {
  const { pnl, pnlPercent } = useProfitAndLoss(type)
  return (
    <Typography
      variant="Text md/Regular"
      color={getChangeTextColor({ change: pnl })}
      data-testid={testId}
    >
      {formatProfitAndLoss(pnl)} ({formatPercentageSigned(pnlPercent)})
    </Typography>
  )
}

interface ExpandedHeaderInfoFieldProps {
  valueName: string
}

const ExpandedHeaderInfoField = ({
  children,
  valueName,
}: PropsWithChildren<ExpandedHeaderInfoFieldProps>) => {
  const { t } = useTranslation()
  return (
    <Stack flexBasis="50%" gap="xxs">
      <Typography variant="Text lg/Semibold" component="h2">
        {t(valueName)}
      </Typography>

      <Subscribe fallback={<Skeleton />}>{children}</Subscribe>
    </Stack>
  )
}

export const AppHeader = () => {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <Stack paddingX="2xl" paddingTop="2xl" gap="lg" color="text.primary">
        <Stack direction="row">
          <Stack
            direction="row"
            alignItems="center"
            sx={{ cursor: "pointer" }}
            gap="md"
            flexGrow={1}
            data-testid={AppHeaderLocators.main}
            onClick={() => setExpanded(!expanded)}
          >
            <LogoShort />
            <Box>
              <Typography
                variant="Text xl/Semibold"
                component="h1"
                sx={{ lineHeight: "normal" }}
                data-testid={AppHeaderLocators.accountName}
              >
                Adaptive
              </Typography>

              <Typography
                variant="Text lg/Semibold"
                sx={{ lineHeight: "normal" }}
                data-testid={AppHeaderLocators.accountInfo}
              >
                <Subscribe fallback={<Skeleton sx={{ width: "100%" }} />}>
                  {expanded ? <AccountNumber /> : <PortfolioBalance />}
                </Subscribe>
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Collapse in={expanded} data-testid={AppHeaderLocators.expanded}>
          <Stack gap="md">
            <Stack direction="row" gap="lg">
              <ExpandedHeaderInfoField valueName="portfolioValue">
                <PortfolioBalance />
              </ExpandedHeaderInfoField>
              <ExpandedHeaderInfoField valueName="cashValue">
                <CashBalance />
              </ExpandedHeaderInfoField>
            </Stack>
            <Stack direction="row" gap="lg">
              <ExpandedHeaderInfoField valueName="totalGainLoss">
                <ProfitAndLoss
                  type="total"
                  data-testid={AppHeaderLocators.totalGainLoss}
                />
              </ExpandedHeaderInfoField>
              <ExpandedHeaderInfoField valueName="dailyGainLoss">
                <ProfitAndLoss
                  type="daily"
                  data-testid={AppHeaderLocators.dailyGainLoss}
                />
              </ExpandedHeaderInfoField>
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </>
  )
}
